import { Modal } from 'antd'
import { Compass, Lightbulb } from 'lucide-react'
import styled from 'styled-components'
import type { InterviewSummaryStructured, SurveySummaryStructured } from '@bandos/shared'
import { SurveySummaryCard } from '../../features/survey/components/insights/SurveySummaryCard'
import { COLORS, SPACING, BORDER_RADIUS, TYPOGRAPHY, FONT_WEIGHT } from '../../styles/theme'
import type { UploadedResearchItem } from './UploadedResearchCard'

// ─── Styles ───────────────────────────────────────────────────────────────────

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.lg};
  padding: ${SPACING.sm} 0;
`

const InterviewCard = styled.div`
  background: var(--color-bg-card);
  border: 1px solid var(--color-border);
  border-top: 3px solid ${COLORS.purple500};
  border-radius: ${BORDER_RADIUS.lg};
  overflow: hidden;
`

const InterviewCardHeader = styled.div`
  padding: ${SPACING.lg} ${SPACING.xl};
  border-bottom: 1px solid var(--color-border);
`

const InterviewCardTitle = styled.h3`
  font-size: ${TYPOGRAPHY.body.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  color: var(--color-text-primary);
  margin: 0;
`

const QuoteBlock = styled.blockquote`
  margin: 0;
  padding: ${SPACING.xl};
  background: color-mix(in srgb, ${COLORS.purple500} 6%, transparent);
  border-bottom: 1px solid var(--color-border);
`

const QuoteMark = styled.span`
  display: block;
  font-size: 2.5rem;
  line-height: 1;
  color: ${COLORS.purple500};
  font-family: Georgia, serif;
  margin-bottom: ${SPACING.xs};
  user-select: none;
`

const QuoteText = styled.p`
  font-size: ${TYPOGRAPHY.body.fontSize};
  font-style: italic;
  color: var(--color-text-primary);
  line-height: 1.65;
  margin: 0;
`

const SectionsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0;
`

const Section = styled.div`
  padding: ${SPACING.xl};

  & + & {
    border-top: 1px solid var(--color-border);
  }
`

const SectionTitle = styled.h4`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  color: var(--color-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 ${SPACING.md};
`

const FactList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xs};
`

const FactLine = styled.li`
  font-size: ${TYPOGRAPHY.body.fontSize};
  color: var(--color-text-primary);
  line-height: 1.55;
  padding-left: ${SPACING.md};
  position: relative;

  &::before {
    content: '·';
    position: absolute;
    left: 0;
    color: var(--color-text-secondary);
  }
`

const ItemList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${SPACING.sm};
`

const ListItem = styled.li`
  font-size: ${TYPOGRAPHY.body.fontSize};
  color: var(--color-text-primary);
  line-height: 1.55;
  padding-left: ${SPACING.md};
  position: relative;

  &::before {
    content: '·';
    position: absolute;
    left: 0;
    color: var(--color-text-secondary);
  }
`

// ─── Single interview summary display ────────────────────────────────────────

interface InterviewDocumentSummaryProps {
  summary: InterviewSummaryStructured
  index: number
  total: number
}

function InterviewDocumentSummary({ summary, index, total }: InterviewDocumentSummaryProps) {
  return (
    <InterviewCard>
      {total > 1 && (
        <InterviewCardHeader>
          <InterviewCardTitle>Interview {index + 1}</InterviewCardTitle>
        </InterviewCardHeader>
      )}

      {summary.quote && (
        <QuoteBlock>
          <QuoteMark>&quot;</QuoteMark>
          <QuoteText>{summary.quote}</QuoteText>
        </QuoteBlock>
      )}

      <SectionsGrid>
        {summary.quickFacts.length > 0 && (
          <Section>
            <SectionTitle>About this person</SectionTitle>
            <FactList>
              {summary.quickFacts.map((fact, i) => (
                <FactLine key={i}>{fact}</FactLine>
              ))}
            </FactList>
          </Section>
        )}

        {summary.insights.length > 0 && (
          <Section>
            <SectionTitle>
              <Lightbulb size={13} />
              Insights
            </SectionTitle>
            <ItemList>
              {summary.insights.map((item, i) => (
                <ListItem key={i}>{item}</ListItem>
              ))}
            </ItemList>
          </Section>
        )}

        {summary.opportunities.length > 0 && (
          <Section>
            <SectionTitle>
              <Compass size={13} />
              Unmet needs
            </SectionTitle>
            <ItemList>
              {summary.opportunities.map((item, i) => (
                <ListItem key={i}>{item}</ListItem>
              ))}
            </ItemList>
          </Section>
        )}
      </SectionsGrid>
    </InterviewCard>
  )
}

// ─── Modal ────────────────────────────────────────────────────────────────────

interface DocumentSummaryModalProps {
  item: UploadedResearchItem | null
  onClose: () => void
}

export function DocumentSummaryModal({ item, onClose }: DocumentSummaryModalProps) {
  const isSurvey = item?.ai_document_type === 'survey'
  const isInterview = item?.ai_document_type === 'interview'

  return (
    <Modal
      open={item !== null}
      title={item?.file_name ?? ''}
      onCancel={onClose}
      footer={null}
      width={640}
      destroyOnClose
    >
      {item?.ai_summary_structured && (
        <ModalContent>
          {isSurvey && (
            <SurveySummaryCard
              structured={item.ai_summary_structured as SurveySummaryStructured}
              generatedAt={null}
              responseCount={null}
              isPending={false}
            />
          )}

          {isInterview &&
            (item.ai_summary_structured as InterviewSummaryStructured[]).map((summary, i, arr) => (
              <InterviewDocumentSummary key={i} summary={summary} index={i} total={arr.length} />
            ))}
        </ModalContent>
      )}
    </Modal>
  )
}
